import Axios from "axios";
/**
 * Type-safe API error wrapping an AxiosError with a ProblemDetails body.
 */
export class ApiError extends Error {
    status;
    problemDetails;
    requestId;
    retryAfterSeconds;
    constructor(error) {
        const problem = error.response?.data ?? null;
        const message = problem?.detail ?? error.message;
        super(message);
        this.name = "ApiError";
        this.status = error.response?.status ?? 0;
        this.problemDetails = problem;
        this.requestId =
            error.response?.headers?.["x-request-id"] ?? null;
        const retryAfter = error.response?.headers?.["retry-after"];
        this.retryAfterSeconds = retryAfter ? Number(retryAfter) : null;
    }
}
let accessTokenProvider = null;
/**
 * Register a callback that returns the current access token.
 *
 * Call this once at application startup to wire up your auth layer:
 *
 * **Web (MSAL.js):**
 * ```ts
 * import { getAccessToken } from '@/lib/auth/getAccessToken';
 * setAccessTokenProvider(getAccessToken);
 * ```
 *
 * **React Native (Keychain):**
 * ```ts
 * import { getTokens } from '@services/storage/secureTokens';
 * setAccessTokenProvider(async () => {
 *   const tokens = await getTokens();
 *   return tokens?.accessToken ?? '';
 * });
 * ```
 */
export function setAccessTokenProvider(provider) {
    accessTokenProvider = provider;
}
let tokenRefreshHandler = null;
/**
 * Register a callback that triggers silent token refresh on 401.
 *
 * This is called when the API returns a 401 Unauthorized response.
 * The handler should attempt to refresh the token (e.g. via MSAL
 * acquireTokenSilent or the /auth/refresh endpoint) and throw if
 * refresh is not possible (forcing a re-login).
 */
export function setTokenRefreshHandler(handler) {
    tokenRefreshHandler = handler;
}
let rateLimitHandler = null;
/**
 * Register a callback invoked when the API returns 429 Too Many Requests.
 *
 * Use this to show a user-facing "please wait" message or to queue retries.
 * The `retryAfterSeconds` value comes from the `Retry-After` response header.
 */
export function setRateLimitHandler(handler) {
    rateLimitHandler = handler;
}
// ---------------------------------------------------------------------------
// UUID v4 generator (minimal, no dependency)
// ---------------------------------------------------------------------------
function generateRequestId() {
    // crypto.randomUUID is available in modern browsers and Node 19+.
    // Fall back to a manual implementation for React Native / older runtimes.
    if (typeof globalThis.crypto !== "undefined" &&
        typeof globalThis.crypto.randomUUID === "function") {
        return globalThis.crypto.randomUUID();
    }
    // Fallback: RFC 4122 version 4 UUID using Math.random
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------
// Allow Vite apps to override via VITE_API_BASE_URL env var at build time.
// Falls back to same-origin /api/v1 for local dev and path-based routing.
// Uses globalThis.__VITE_ENV__ (injected by Vite's define plugin at build time)
// to avoid import.meta which is unsupported in React Native's Hermes engine.
const apiBaseUrl = (() => {
    try {
        // Vite injects process.env.VITE_* at build time
        if (typeof process !== "undefined" && process.env?.VITE_API_BASE_URL) {
            return process.env.VITE_API_BASE_URL;
        }
    }
    catch {
        // process not available
    }
    return "/api/v1";
})();
export const AXIOS_INSTANCE = Axios.create({
    baseURL: apiBaseUrl,
    timeout: 30_000,
    headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
    },
});
// ---------------------------------------------------------------------------
// Request interceptors
// ---------------------------------------------------------------------------
// 1. Attach Authorization Bearer token
AXIOS_INSTANCE.interceptors.request.use(async (config) => {
    if (accessTokenProvider) {
        try {
            const token = await accessTokenProvider();
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        catch {
            // If token acquisition fails silently, let the request proceed
            // without auth -- the API will return 401 and the response
            // interceptor will handle it.
        }
    }
    return config;
});
// 2. Attach X-Request-Id for request correlation
AXIOS_INSTANCE.interceptors.request.use((config) => {
    config.headers["X-Request-Id"] = generateRequestId();
    return config;
});
// ---------------------------------------------------------------------------
// Response interceptors
// ---------------------------------------------------------------------------
// Track in-flight token refresh to avoid concurrent refresh attempts
let refreshPromise = null;
AXIOS_INSTANCE.interceptors.response.use(
// Success: pass through
(response) => response, 
// Error: handle 401 and 429, wrap all errors as ApiError
async (error) => {
    const status = error.response?.status;
    // 401 Unauthorized -- attempt token refresh once, then retry
    // Skip refresh logic for the refresh endpoint itself to avoid deadlock
    // (refreshing during a refresh would re-await the same in-flight promise).
    const isRefreshRequest = error.config?.url?.includes("/auth/refresh");
    if (status === 401 && tokenRefreshHandler && !isRefreshRequest) {
        const originalRequest = error.config;
        // Guard against infinite retry loops: only retry once
        if (originalRequest &&
            !originalRequest
                ._retried) {
            originalRequest._retried = true;
            try {
                // Deduplicate concurrent refresh attempts
                if (!refreshPromise) {
                    refreshPromise = tokenRefreshHandler().finally(() => {
                        refreshPromise = null;
                    });
                }
                await refreshPromise;
                // Retry the original request with a fresh token
                return AXIOS_INSTANCE(originalRequest);
            }
            catch {
                // Refresh failed -- propagate the 401 as an ApiError
            }
        }
    }
    // 429 Too Many Requests -- notify the rate-limit handler
    if (status === 429) {
        const retryAfter = error.response?.headers?.["retry-after"];
        const seconds = retryAfter ? Number(retryAfter) : 60;
        if (rateLimitHandler) {
            rateLimitHandler(seconds);
        }
    }
    // Wrap all API errors as ApiError with parsed ProblemDetails
    throw new ApiError(error);
});
// ---------------------------------------------------------------------------
// Custom instance function for Orval
// ---------------------------------------------------------------------------
/**
 * Custom Axios instance function consumed by Orval-generated code.
 *
 * Orval's mutator expects a function with signature:
 *   (config: AxiosRequestConfig) => Promise<T>
 *
 * It extracts `response.data` so generated hooks receive typed data directly.
 */
export const customInstance = (config, options) => {
    const source = Axios.CancelToken.source();
    const promise = AXIOS_INSTANCE({
        ...config,
        ...options,
        cancelToken: source.token,
    }).then(({ data }) => data);
    // Attach cancel method for TanStack Query's signal-based cancellation
    // @ts-expect-error -- cancel is an ad-hoc property used by react-query
    promise.cancel = () => {
        source.cancel("Query was cancelled");
    };
    return promise;
};
export default customInstance;
//# sourceMappingURL=axios-instance.js.map